import { Product } from "@towersystems/roam-common/lib/generated-types";
import {
  MUTATION_ADD_TO_PARENT_PRODUCT,
  QUERY_PRODUCT,
  useSelectedProductEntityContextProvider,
} from "../../utils";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Button,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { ProductActionsRootParamList } from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import { PlaceholderLoadingScreen } from "../PlaceholderLoadingScreen";
import React from "react";
import { AddMainProductForm } from "../../components/AddMainProductForm";
import { useProductFormActionsContextProvider } from "../../components/ProductForm/hooks";
import { useFormContext } from "react-hook-form";
import { AddMainProductFormValues } from "../../components/AddMainProductForm/types";
import { Colors } from "react-native-ui-lib";
import { useAddMainProductFormActionsContextProvider } from "../../components/AddMainProductForm/hooks";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";

export type ProductActionsSelectParentOptionValuesScreenProps =
  StackScreenProps<
    ProductActionsRootParamList,
    "ProductActionsSelectParentOptionValuesScreen"
  >;

const ProductActionsSelectParentOptionValues = ({
  navigation,
}: ProductActionsSelectParentOptionValuesScreenProps) => {
  const [m] = useMutation<{ addToParentProduct: Product }>(
    MUTATION_ADD_TO_PARENT_PRODUCT
  );

  const { handleSubmit, formState, setError, trigger } =
    useFormContext<AddMainProductFormValues>();

  const maybeActions = useAddMainProductFormActionsContextProvider();

  const {
    errors,
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
  } = formState;

  const flashMessagesContext = useFlashMessages();

  const handleOnSubmit = React.useCallback(
    async (values: AddMainProductFormValues) => {
      try {
        const { data } = await m({
          variables: {
            input: {
              id: values.id,
              variantParentId: values.variantParentId,
              selectedOptions: values.selectedOptions.map((o) => {
                return {
                  name: o.name,
                  value: o.value,
                };
              }),
            },
          },
        });

        if (data?.addToParentProduct && maybeActions?.onAfterSubmitSuccess) {
          await maybeActions.onAfterSubmitSuccess?.(data.addToParentProduct);
        } else {
          throw new Error("Something went wrong");
        }
      } catch (err: any) {
        const msg = err?.message ?? "Something went wrong";
        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });
        setError("root", {
          type: "serverError",
          message: msg,
        });
      }
    },
    []
  );

  React.useEffect(() => {
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  const disabled =
    !isDirty ||
    isLoading ||
    isSubmitting ||
    isValid == false ||
    isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label="Update Product"
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title="Enter product options" footer={footerMarkup}>
      <AddMainProductForm />
    </Page>
  );
};

export const ProductActionsSelectParentOptionValuesScreen = (
  props: ProductActionsSelectParentOptionValuesScreenProps
) => {
  const selectedProduct = useSelectedProductEntityContextProvider();
  const { navigation, route } = props;
  const transitionEnded = useHasTransitionEnded(navigation);

  const [q, { loading, data }] = useLazyQuery<{ product: Product }>(
    QUERY_PRODUCT
  );

  const { productId } = route.params;

  React.useEffect(() => {
    q({
      variables: {
        id: productId,
      },
    });
  }, []);

  if (
    loading ||
    !data?.product ||
    false == transitionEnded ||
    !selectedProduct.product
  ) {
    return (
      <Page>
        <PlaceholderLoadingScreen
          navigation={navigation}
          modifyHeader={false}
          content={ResourcePageSkeleton}
        />
      </Page>
    );
  }

  const defaultValues = {
    id: selectedProduct.product?.id,
    variantParentId: productId,
    selectedOptions: data.product.options.map((o) => {
      return {
        name: o.name,
        value: "",
      };
    }),
  };

  return (
    <AddMainProductForm.AddMainProductFormProvider
      defaultValues={defaultValues}
    >
      <ProductActionsSelectParentOptionValues
        {...props}
      ></ProductActionsSelectParentOptionValues>
    </AddMainProductForm.AddMainProductFormProvider>
  );
};
